// FlowChart.js
import React, { useState, useEffect } from 'react';
import ReactFlow, { Controls, Background, ReactFlowProvider, Handle } from 'react-flow-renderer';
import dagre from 'dagre';

// Define the FlowChart component
const FlowChart = ({ jsonData, userJourneyData }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    if (jsonData && userJourneyData) {
      processFlowData();
    }
  }, [jsonData, userJourneyData]);

  const processFlowData = () => {
    const { nodes: journeyNodes, edges: journeyEdges } = processUserJourneyData(userJourneyData);
    const updatedNodes = journeyNodes.map((node) => {
      // Attach jsonData properties to nodes
      const matchingNodeData = findNodeData(jsonData, node.data.label);
      return { ...node, data: { ...node.data, ...matchingNodeData } };
    });
    const layoutedElements = getLayoutedElements(updatedNodes, journeyEdges);
    setNodes(layoutedElements.nodes);
    setEdges(layoutedElements.edges);
  };

  // Layout using Dagre to improve spacing
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 200;
  const nodeHeight = 50;

  const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    const isHorizontal = direction === 'LR';
    dagreGraph.setGraph({ rankdir: direction });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.targetPosition = isHorizontal ? 'left' : 'top';
      node.sourcePosition = isHorizontal ? 'right' : 'bottom';

      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
    });

    return { nodes, edges };
  };

  // Find matching node details in the jsonData
  const findNodeData = (data, url) => {
    const findRecursive = (children) => {
      for (const child of children) {
        if (child.name === url) {
          return child;
        }
        if (child.children) {
          const found = findRecursive(child.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findRecursive(data.children || []);
  };

  // Handle node click event to allow editing of node data
  const handleNodeClick = (event, node) => {
    setSelectedNode(node);
  };

  // Handle input change for node editing
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedNode((prevNode) => ({
      ...prevNode,
      data: { ...prevNode.data, [name]: value },
    }));
  };

  // Function to update node data back to the state
  const handleSaveNode = () => {
    const updatedNode = selectedNode;
    setNodes((prevNodes) =>
      prevNodes.map((node) => (node.id === updatedNode.id ? updatedNode : node))
    );
    setSelectedNode(null);
  };

  return (
    <div style={{ width: '100%', height: '600px' }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={handleNodeClick}
          fitView
        >
          <Controls />
          <Background gap={16} />
        </ReactFlow>
      </ReactFlowProvider>
      {selectedNode && (
        <div className="node-details">
          <h5>Node Details (Editable)</h5>
          <input
            type="text"
            name="action"
            value={selectedNode.data.action || ''}
            onChange={handleInputChange}
            placeholder="Action"
          />
          <input
            type="text"
            name="description"
            value={selectedNode.data.description || ''}
            onChange={handleInputChange}
            placeholder="Description"
          />
          <input
            type="text"
            name="image_url"
            value={selectedNode.data.image_url || ''}
            onChange={handleInputChange}
            placeholder="Image URL"
          />
          <input
            type="text"
            name="validation"
            value={selectedNode.data.validation || ''}
            onChange={handleInputChange}
            placeholder="Validation"
          />
          <button onClick={handleSaveNode}>Save Node</button>
        </div>
      )}
    </div>
  );
};

// Helper function to process user journey data into nodes and edges
const processUserJourneyData = (journeyData) => {
  const nodes = [];
  const edges = [];
  const urlToNodeId = {};
  let nodeId = 0;

  // Add startUrl as node
  const startUrl = journeyData.startUrl;
  urlToNodeId[startUrl] = `${nodeId}`;
  nodes.push({
    id: `${nodeId}`,
    data: { label: startUrl },
    position: { x: Math.random() * 600, y: Math.random() * 400 },
  });
  nodeId++;

  // Process each step in the user journey path
  journeyData.path.forEach((step) => {
    const { from, to, action } = step;

    // Add from and to URLs as nodes
    if (!(from in urlToNodeId)) {
      urlToNodeId[from] = `${nodeId}`;
      nodes.push({ id: `${nodeId}`, data: { label: from }, position: { x: Math.random() * 600, y: Math.random() * 400 } });
      nodeId++;
    }
    if (!(to in urlToNodeId)) {
      urlToNodeId[to] = `${nodeId}`;
      nodes.push({ id: `${nodeId}`, data: { label: to }, position: { x: Math.random() * 600, y: Math.random() * 400 } });
      nodeId++;
    }

    // Add edge with action as label
    edges.push({
      id: `e${urlToNodeId[from]}-${urlToNodeId[to]}`,
      source: urlToNodeId[from],
      target: urlToNodeId[to],
      label: action,
      animated: true,
      style: { stroke: '#0077b6' },
    });
  });

  return { nodes, edges };
};

export default FlowChart;
